/**
 * FormControls.tsx
 * Form controls category page - All interactive form elements organized by category
 * 
 * Showcases all form-related HeroUI components with accordion-organized layout
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter, Accordion, AccordionItem } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Input } from '@enfomentis/ui';
import { 
  MousePointer2, 
  Type, 
  Hash, 
  FileText, 
  ChevronDown, 
  List, 
  CheckSquare, 
  Circle,
  MoreHorizontal,
  Menu,
  ToggleLeft,
  SlidersHorizontal,
  Calendar,
  Clock
} from 'lucide-react';

export const FormControls: React.FC = () => {
  const navigate = useNavigate();

  // Organize components by logical categories
  const componentCategories = {
    'Action Controls': [
      {
        name: 'Button',
        description: 'Clickable buttons with variants and interactive states',
        status: 'available',
        heroUIBase: 'Button',
        route: '/playground/components/forms/button',
        icon: MousePointer2,
        preview: (
          <div className="flex gap-2 items-center justify-center">
            <Button color="primary" size="sm">Primary</Button>
            <Button variant="bordered" color="secondary" size="sm">Secondary</Button>
          </div>
        )
      },
      {
        name: 'ButtonGroup',
        description: 'Multi-option selection with button groups',
        status: 'available',
        heroUIBase: 'ButtonGroup',
        route: '/playground/components/forms/button-group',
        icon: MoreHorizontal,
        preview: (
          <div className="flex gap-1 border border-border rounded">
            <button className="px-2 py-1 text-xs bg-primary text-primary-foreground">Option 1</button>
            <button className="px-2 py-1 text-xs hover:bg-hover">Option 2</button>
            <button className="px-2 py-1 text-xs hover:bg-hover">Option 3</button>
          </div>
        )
      }
    ],
    
    'Text & Number Inputs': [
      {
        name: 'Input',
        description: 'Text input with variants, validation, and customization',
        status: 'available',
        heroUIBase: 'Input',
        route: '/playground/components/forms/input',
        icon: Type,
        preview: (
          <div className="space-y-2">
            <Input 
              variant="bordered" 
              size="sm"
              placeholder="Type here..."
              className="text-xs"
            />
          </div>
        )
      },
      {
        name: 'NumberInput',
        description: 'Numeric input with increment/decrement controls and validation',
        status: 'available',
        heroUIBase: 'NumberInput',
        route: '/playground/components/forms/number-input',
        icon: Hash,
        preview: (
          <div className="flex items-center gap-1 p-2 border border-border rounded text-xs">
            <span>$</span>
            <div className="flex-1 text-center">1,234.56</div>
            <div className="flex flex-col">
              <div className="w-3 h-2 bg-primary/20 rounded-t text-[8px] flex items-center justify-center">▲</div>
              <div className="w-3 h-2 bg-primary/20 rounded-b text-[8px] flex items-center justify-center">▼</div>
            </div>
          </div>
        )
      },
      {
        name: 'TextArea',
        description: 'Multi-line text input with auto-resize and validation',
        status: 'available',
        heroUIBase: 'Textarea',
        route: '/playground/components/forms/textarea',
        icon: FileText,
        preview: (
          <div className="border border-border rounded p-2 text-xs min-h-[2.5rem] bg-background">
            <div className="text-text-secondary">Multi-line text...</div>
            <div className="text-text-secondary">Line two...</div>
          </div>
        )
      }
    ],

    'Selection & Dropdown Controls': [
      {
        name: 'Select',
        description: 'Dropdown selection with search, sections, and multi-select support',
        status: 'available',
        heroUIBase: 'Select',
        route: '/playground/components/forms/select',
        icon: ChevronDown,
        preview: (
          <div className="flex items-center justify-between p-2 border border-border rounded text-xs">
            <span className="text-text-secondary">Choose option...</span>
            <ChevronDown className="w-3 h-3 text-text-tertiary" />
          </div>
        )
      },
      {
        name: 'Dropdown',
        description: 'Context menus and action lists with keyboard navigation',
        status: 'available',
        heroUIBase: 'Dropdown',
        route: '/playground/components/forms/dropdown',
        icon: MoreHorizontal,
        preview: (
          <div className="space-y-1">
            <div className="flex items-center gap-2 p-1 text-xs hover:bg-hover rounded">
              <div className="w-2 h-2 bg-primary rounded"></div>
              <span>Action 1</span>
            </div>
            <div className="flex items-center gap-2 p-1 text-xs hover:bg-hover rounded">
              <div className="w-2 h-2 bg-secondary rounded"></div>
              <span>Action 2</span>
            </div>
          </div>
        )
      },
      {
        name: 'Listbox',
        description: 'Selectable lists with single/multi-selection and keyboard support',
        status: 'available',
        heroUIBase: 'Listbox',
        route: '/playground/components/forms/listbox',
        icon: List,
        preview: (
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2 p-1 bg-primary/10 rounded">
              <CheckSquare className="w-3 h-3 text-primary" />
              <span>Selected Item</span>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-hover rounded">
              <div className="w-3 h-3 border border-border rounded"></div>
              <span>Option 2</span>
            </div>
          </div>
        )
      }
    ],

    'Choice & Toggle Controls': [
      {
        name: 'Checkbox',
        description: 'Individual checkboxes and grouped checkbox selections',
        status: 'available',
        heroUIBase: 'Checkbox',
        route: '/playground/components/forms/checkbox',
        icon: CheckSquare,
        preview: (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-sm"></div>
              </div>
              <span>Option 1</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 border border-border rounded"></div>
              <span>Option 2</span>
            </div>
          </div>
        )
      },
      {
        name: 'CheckboxGroup',
        description: 'Grouped checkbox selections with select all functionality',
        status: 'available',
        heroUIBase: 'CheckboxGroup',
        route: '/playground/components/forms/checkbox-group',
        icon: Menu,
        preview: (
          <div className="space-y-1 text-xs">
            <div className="font-medium text-text-primary mb-1">Assets</div>
            <div className="flex items-center gap-2">
              <CheckSquare className="w-3 h-3 text-primary" />
              <span>Stocks</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckSquare className="w-3 h-3 text-primary" />
              <span>Options</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border border-border rounded"></div>
              <span>Futures</span>
            </div>
          </div>
        )
      },
      {
        name: 'RadioGroup',
        description: 'Single-selection radio button groups with customization',
        status: 'available',
        heroUIBase: 'RadioGroup',
        route: '/playground/components/forms/radio-group',
        icon: Circle,
        preview: (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 border-2 border-primary rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
              </div>
              <span>Option A</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 border-2 border-border rounded-full"></div>
              <span>Option B</span>
            </div>
          </div>
        )
      },
      {
        name: 'Switch',
        description: 'Toggle switches for boolean states with size and color variants',
        status: 'available',
        heroUIBase: 'Switch',
        route: '/playground/components/forms/switch',
        icon: ToggleLeft,
        preview: (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-4 bg-border rounded-full relative">
                <div className="w-3 h-3 bg-white rounded-full absolute top-0.5 left-0.5 shadow"></div>
              </div>
              <span className="text-xs">Off</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-4 bg-primary rounded-full relative">
                <div className="w-3 h-3 bg-white rounded-full absolute top-0.5 right-0.5 shadow"></div>
              </div>
              <span className="text-xs">On</span>
            </div>
          </div>
        )
      }
    ],

    'Range & Slider Controls': [
      {
        name: 'Slider',
        description: 'Range sliders with single/dual handles, vertical/horizontal orientations',
        status: 'available',
        heroUIBase: 'Slider',
        route: '/playground/components/forms/slider',
        icon: SlidersHorizontal,
        preview: (
          <div className="space-y-3 w-full">
            <div className="relative w-full">
              <div className="h-2 bg-border rounded-full relative">
                <div className="h-2 bg-primary rounded-full w-3/5"></div>
                <div className="absolute right-2/5 top-1/2 w-4 h-4 bg-primary rounded-full transform -translate-y-1/2 -translate-x-1/2"></div>
              </div>
              <span className="text-sm">Single: 60%</span>
            </div>
            <div className="relative w-full">
              <div className="h-2 bg-border rounded-full relative">
                <div className="h-2 bg-secondary rounded-full absolute left-1/4 w-2/4"></div>
                <div className="absolute left-1/4 top-1/2 w-3 h-3 bg-secondary rounded-full transform -translate-y-1/2 -translate-x-1/2"></div>
                <div className="absolute left-3/4 top-1/2 w-3 h-3 bg-secondary rounded-full transform -translate-y-1/2 -translate-x-1/2"></div>
              </div>
              <span className="text-sm">Range: 25% - 75%</span>
            </div>
            <div className="text-text-tertiary text-center">Range Selection</div>
          </div>
        )
      }
    ],

    'Date & Time Controls': [
      {
        name: 'DateInput',
        description: 'Date input fields with segment editing and internationalization support',
        status: 'available',
        heroUIBase: 'DateInput',
        route: '/playground/components/forms/date-input',
        icon: Calendar,
        preview: (
          <div className="text-xs space-y-2 w-full">
            <div className="space-y-2">
              <div className="flex items-center gap-1 p-2 border border-border rounded">
                <div className="px-2 py-1 bg-primary/10 rounded text-xs">12</div>
                <span>/</span>
                <div className="px-2 py-1 bg-primary/10 rounded text-xs">25</div>
                <span>/</span>
                <div className="px-2 py-1 bg-primary/10 rounded text-xs">2024</div>
              </div>
              <span className="text-sm">MM/DD/YYYY Format</span>
              
              <div className="flex items-center gap-1 p-2 border border-secondary rounded">
                <div className="px-2 py-1 bg-secondary/10 rounded text-xs">25</div>
                <span>/</span>
                <div className="px-2 py-1 bg-secondary/10 rounded text-xs">12</div>
                <span>/</span>
                <div className="px-2 py-1 bg-secondary/10 rounded text-xs">2024</div>
                <span className="mx-1">•</span>
                <div className="px-1 py-1 bg-secondary/10 rounded text-xs">14:30</div>
              </div>
              <span className="text-sm">With Time</span>
            </div>
            <div className="text-text-tertiary text-center">Date Input</div>
          </div>
        )
      },
      {
        name: 'TimeInput',
        description: 'Time input with segment editing, 12/24 hour formats, and precision control',
        status: 'available',
        heroUIBase: 'TimeInput',
        route: '/playground/components/forms/time-input',
        icon: Clock,
        preview: (
          <div className="text-xs space-y-2 w-full">
            <div className="space-y-2">
              <div className="flex items-center gap-1 p-2 border border-border rounded">
                <div className="px-2 py-1 bg-primary/10 rounded text-xs">02</div>
                <span>:</span>
                <div className="px-2 py-1 bg-primary/10 rounded text-xs">30</div>
                <div className="px-2 py-1 bg-primary/10 rounded text-xs ml-2">PM</div>
              </div>
              <span className="text-sm">12-hour format</span>
              
              <div className="flex items-center gap-1 p-2 border border-secondary rounded">
                <div className="px-2 py-1 bg-secondary/10 rounded text-xs">14</div>
                <span>:</span>
                <div className="px-2 py-1 bg-secondary/10 rounded text-xs">30</div>
                <span>:</span>
                <div className="px-2 py-1 bg-secondary/10 rounded text-xs">45</div>
              </div>
              <span className="text-sm">24-hour with seconds</span>
            </div>
            <div className="text-text-tertiary text-center">Time Input</div>
          </div>
        )
      },
      {
        name: 'Calendar',
        description: 'Calendar widget with date selection, constraints, and internationalization',
        status: 'available',
        heroUIBase: 'Calendar',
        route: '/playground/components/forms/calendar',
        icon: Calendar,
        preview: (
          <div className="text-xs space-y-2 w-full">
            <div className="space-y-2">
              <div className="grid grid-cols-7 gap-1 text-center">
                <div className="text-xs font-medium">S</div>
                <div className="text-xs font-medium">M</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">W</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">F</div>
                <div className="text-xs font-medium">S</div>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                <div className="text-xs text-text-tertiary">1</div>
                <div className="text-xs text-text-tertiary">2</div>
                <div className="text-xs text-text-tertiary">3</div>
                <div className="text-xs text-text-tertiary">4</div>
                <div className="text-xs text-text-tertiary">5</div>
                <div className="text-xs text-text-tertiary">6</div>
                <div className="text-xs text-text-tertiary">7</div>
                <div className="text-xs text-text-tertiary">8</div>
                <div className="text-xs text-text-tertiary">9</div>
                <div className="text-xs text-text-tertiary">10</div>
                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center text-xs text-white">15</div>
                <div className="text-xs text-text-tertiary">16</div>
                <div className="text-xs text-text-tertiary">17</div>
                <div className="text-xs text-text-tertiary">18</div>
              </div>
            </div>
            <div className="text-text-tertiary text-center">Calendar Widget</div>
          </div>
        )
      },
      {
        name: 'RangeCalendar',
        description: 'Date range selection with start/end dates, multiple months, and non-contiguous range support',
        status: 'available',
        heroUIBase: 'RangeCalendar',
        route: '/playground/components/forms/range-calendar',
        icon: Calendar,
        preview: (
          <div className="text-xs space-y-2 w-full">
            <div className="space-y-2">
              <div className="grid grid-cols-7 gap-1 text-center">
                <div className="text-xs font-medium">S</div>
                <div className="text-xs font-medium">M</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">W</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">F</div>
                <div className="text-xs font-medium">S</div>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                <div className="text-xs text-text-tertiary">1</div>
                <div className="text-xs text-text-tertiary">2</div>
                <div className="text-xs text-text-tertiary">3</div>
                <div className="text-xs text-text-tertiary">4</div>
                <div className="text-xs text-text-tertiary">5</div>
                <div className="text-xs text-text-tertiary">6</div>
                <div className="text-xs text-text-tertiary">7</div>
                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center text-xs text-white">8</div>
                <div className="text-xs">9</div>
                <div className="text-xs bg-primary/20 rounded">10</div>
                <div className="text-xs bg-primary/20 rounded">11</div>
                <div className="text-xs bg-primary/20 rounded">12</div>
                <div className="text-xs bg-primary/20 rounded">13</div>
                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center text-xs text-white">14</div>
              </div>
            </div>
            <div className="text-text-tertiary text-center">Range Selection</div>
          </div>
        )
      },
      {
        name: 'DatePicker',
        description: 'Date picker with popup calendar, time granularity, and internationalization support',
        status: 'available',
        heroUIBase: 'DatePicker',
        route: '/playground/components/forms/date-picker',
        icon: Calendar,
        preview: (
          <div className="text-xs space-y-2 w-full">
            <div className="space-y-2">
              <div className="p-2 border border-border rounded flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="px-2 py-1 bg-primary/10 rounded text-xs">12</div>
                  <span>/</span>
                  <div className="px-2 py-1 bg-primary/10 rounded text-xs">25</div>
                  <span>/</span>
                  <div className="px-2 py-1 bg-primary/10 rounded text-xs">2024</div>
                </div>
                <Calendar size={12} className="text-text-tertiary" />
              </div>
              <span className="text-sm">Input + Calendar Popup</span>
              
              <div className="grid grid-cols-7 gap-1 text-center p-2 bg-background/50 rounded">
                <div className="text-xs font-medium">S</div>
                <div className="text-xs font-medium">M</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">W</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">F</div>
                <div className="text-xs font-medium">S</div>
                <div className="text-xs text-text-tertiary">22</div>
                <div className="text-xs text-text-tertiary">23</div>
                <div className="text-xs text-text-tertiary">24</div>
                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center text-xs text-white">25</div>
                <div className="text-xs">26</div>
                <div className="text-xs">27</div>
                <div className="text-xs">28</div>
              </div>
            </div>
            <div className="text-text-tertiary text-center">Date Picker</div>
          </div>
        )
      },
      {
        name: 'DateRangePicker',
        description: 'Date range picker with popup calendar, multiple months display, time granularity and internationalization',
        status: 'available',
        heroUIBase: 'DateRangePicker',
        route: '/playground/components/forms/date-range-picker',
        icon: Calendar,
        preview: (
          <div className="text-xs space-y-2 w-full">
            <div className="space-y-2">
              <div className="p-2 border border-border rounded flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <div className="px-2 py-1 bg-primary/10 rounded text-xs">12/01</div>
                  <span>-</span>
                  <div className="px-2 py-1 bg-primary/10 rounded text-xs">12/15</div>
                </div>
                <Calendar size={12} className="text-text-tertiary" />
              </div>
              <span className="text-sm">Range Input + Calendar</span>
              
              <div className="grid grid-cols-7 gap-1 text-center p-2 bg-background/50 rounded">
                <div className="text-xs font-medium">S</div>
                <div className="text-xs font-medium">M</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">W</div>
                <div className="text-xs font-medium">T</div>
                <div className="text-xs font-medium">F</div>
                <div className="text-xs font-medium">S</div>
                <div className="text-xs text-text-tertiary">29</div>
                <div className="text-xs text-text-tertiary">30</div>
                <div className="w-5 h-5 bg-primary rounded-l-full flex items-center justify-center text-xs text-white">1</div>
                <div className="text-xs bg-primary/20">2</div>
                <div className="text-xs bg-primary/20">14</div>
                <div className="w-5 h-5 bg-primary rounded-r-full flex items-center justify-center text-xs text-white">15</div>
                <div className="text-xs text-text-tertiary">16</div>
              </div>
            </div>
            <div className="text-text-tertiary text-center">Date Range Picker</div>
          </div>
        )
      }
    ]
  };

  // Get total component count
  const totalComponents = Object.values(componentCategories).reduce((total, category) => total + category.length, 0);

  const handleComponentClick = (route: string) => {
    navigate(route);
  };

  interface ComponentInfo {
    name: string;
    description: string;
    status: string;
    heroUIBase: string;
    route: string;
    icon: React.ComponentType<Record<string, unknown>>;
    preview: React.ReactNode;
  }

  const renderComponentCard = (component: ComponentInfo) => {
    const IconComponent = component.icon;
    
    return (
      <Card
        key={component.name}
        isPressable
        onPress={() => handleComponentClick(component.route)}
        className="group hover:shadow-lg transition-all duration-200"
      >
        <CardHeader>
          <div className="flex items-start justify-between w-full gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <IconComponent className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-semibold text-primary text-base md:text-lg truncate">{component.name}</h3>
            </div>
            <Chip 
              color={component.status === 'available' ? 'success' : 'default'}
              size="sm"
              variant="flat"
              className="flex-shrink-0"
            >
              {component.status}
            </Chip>
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          <p className="text-text-secondary text-sm mb-4 leading-relaxed">{component.description}</p>
          
          {/* Component Preview */}
          <div className="bg-surface border border-border rounded-lg p-4 min-h-[80px] flex items-center justify-center">
            {component.preview}
          </div>
        </CardBody>
        <CardFooter className="pt-0">
          <div className="flex items-center justify-between w-full">
            <Chip size="sm" variant="dot" color="secondary">
              {component.heroUIBase}
            </Chip>
            <Button
              size="sm"
              variant="ghost"
              color="primary"
              className="group-hover:bg-primary/10 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleComponentClick(component.route);
              }}
            >
              Explore →
            </Button>
          </div>
        </CardFooter>
      </Card>
    );
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-6">
        
        {/* Category Header */}
        <div className="text-center px-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MousePointer2 className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">Form Controls</h1>
          </div>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Interactive form elements for user input and data collection.
            Build powerful forms with buttons, inputs, selections, and validation components.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <Badge color="success" variant="flat">{totalComponents} components</Badge>
            <Chip size="sm" color="primary" variant="dot">HeroUI Based</Chip>
            <Chip size="sm" color="secondary" variant="dot">{Object.keys(componentCategories).length} categories</Chip>
          </div>
        </div>

        {/* Components organized in Accordions */}
        <div className="max-w-6xl mx-auto px-4">
          <Accordion 
            variant="shadow" 
            selectionMode="multiple"
            defaultExpandedKeys={["0"]} // Expand first category by default
          >
            {Object.entries(componentCategories).map(([categoryName, components], categoryIndex) => (
              <AccordionItem 
                key={categoryIndex}
                title={
                  <div className="flex items-center justify-between w-full pr-4">
                    <span className="text-lg font-semibold">{categoryName}</span>
                    <Badge color="primary" variant="flat" size="sm">
                      {components.length}
                    </Badge>
                  </div>
                }
                className="text-left"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                  {components.map((component) => renderComponentCard(component))}
                </div>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};